// Diretório oficial de mobilidade — leitura pública barata e gestão de admin.
// Sem IA e sem Google Maps: apenas leitura determinística da tabela curada,
// com cache em memória por processo para não gerar custo por roteiro.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  MOBILITY_MODES,
  cacheKey,
  canBeOfficial,
  freshness,
  groupByMode,
  isValidResourceUrl,
  rankResources,
  relevantModes,
  resolveCityKey,
  searchFallback,
  type MobilityMode,
  type MobilityResource,
} from "./directory";

const TTL_MS = 6 * 60 * 60 * 1000; // 6 h
const NEGATIVE_TTL_MS = 60 * 60 * 1000; // 1 h
const cache = new Map<string, { at: number; value: MobilityResource[] }>();

function mapRow(row: Record<string, unknown>): MobilityResource {
  return {
    id: String(row.id),
    cityKey: String(row.city_key),
    cityLabel: String(row.city_label),
    countryCode: (row.country_code as string | null) ?? null,
    mode: row.mode as MobilityMode,
    providerName: String(row.provider_name),
    url: String(row.url),
    official: Boolean(row.official),
    language: (row.language as string | null) ?? null,
    notes: (row.notes as string | null) ?? null,
    priority: Number(row.priority ?? 100),
    active: Boolean(row.active),
    verifiedAt: (row.verified_at as string | null) ?? null,
  };
}

const ModesSchema = z.array(z.enum(MOBILITY_MODES)).max(MOBILITY_MODES.length).optional();

export const getMobilityResources = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        city: z.string().min(2).max(80),
        transport: z.string().min(3).max(12).optional(),
        modes: ModesSchema,
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const modes = (data.modes && data.modes.length
      ? data.modes
      : relevantModes(data.transport ?? "walk")) as MobilityMode[];
    const key = cacheKey(data.city, modes);
    const hit = cache.get(key);
    const now = Date.now();
    if (hit && now - hit.at < (hit.value.length ? TTL_MS : NEGATIVE_TTL_MS)) {
      return {
        city: data.city,
        groups: groupByMode(hit.value, modes),
        fallbacks: modes
          .filter((m) => !hit.value.some((r) => r.mode === m))
          .map((m) => ({ mode: m, url: searchFallback(data.city, m) })),
      };
    }

    const { createClient } = await import("@supabase/supabase-js");
    const sb = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
    const { data: rows } = await sb
      .from("mobility_resources")
      .select(
        "id,city_key,city_label,country_code,mode,provider_name,url,official,language,notes,priority,active,verified_at",
      )
      .eq("city_key", resolveCityKey(data.city))
      .eq("active", true)
      .in("mode", modes)
      .limit(60);

    const ranked = rankResources((rows ?? []).map(mapRow as never), modes, now);
    cache.set(key, { at: now, value: ranked });
    return {
      city: data.city,
      groups: groupByMode(ranked, modes),
      fallbacks: modes
        .filter((m) => !ranked.some((r) => r.mode === m))
        .map((m) => ({ mode: m, url: searchFallback(data.city, m) })),
    };
  });

// ───────────────────────── Gestão (apenas administradores) ─────────────────────────

async function ensureAdmin(userId: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .in("role", ["owner", "admin"])
    .limit(1);
  if (!data || data.length === 0) throw new Error("forbidden");
}

export const listMobilityResources = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ city: z.string().max(80).optional() }).parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let query = supabaseAdmin
      .from("mobility_resources")
      .select("*")
      .order("city_key", { ascending: true })
      .order("priority", { ascending: true })
      .limit(500);
    if (data.city) query = query.eq("city_key", resolveCityKey(data.city));
    const { data: rows, error } = await query;
    if (error) throw new Error(error.message);
    const list = (rows ?? []).map(mapRow as never) as MobilityResource[];
    const now = Date.now();
    return {
      resources: list.map((r) => ({ ...r, freshness: freshness(r.verifiedAt, now) })),
      stats: {
        total: list.length,
        active: list.filter((r) => r.active).length,
        cities: new Set(list.map((r) => r.cityKey)).size,
        stale: list.filter((r) => freshness(r.verifiedAt, now) !== "verified").length,
      },
    };
  });

const UpsertSchema = z.object({
  id: z.string().uuid().optional(),
  city: z.string().min(2).max(80),
  cityLabel: z.string().min(2).max(80),
  countryCode: z.string().length(2).optional(),
  mode: z.enum(MOBILITY_MODES),
  providerName: z.string().min(2).max(120),
  url: z.string().min(8).max(500),
  official: z.boolean().default(true),
  language: z.string().max(5).optional(),
  notes: z.string().max(400).optional(),
  priority: z.number().int().min(1).max(999).default(100),
  active: z.boolean().default(true),
});

export const upsertMobilityResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => UpsertSchema.parse(d))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.userId);
    if (!isValidResourceUrl(data.url)) {
      throw new Error("O endereço tem de ser https e de um domínio válido.");
    }
    if (data.official && !canBeOfficial(data.url)) {
      throw new Error("Este domínio é de revenda: não pode ser marcado como oficial.");
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const payload = {
      city_key: resolveCityKey(data.city),
      city_label: data.cityLabel,
      country_code: data.countryCode ?? null,
      mode: data.mode,
      provider_name: data.providerName,
      url: data.url.trim(),
      official: data.official,
      language: data.language ?? null,
      notes: data.notes ?? null,
      priority: data.priority,
      active: data.active,
      verified_at: new Date().toISOString(),
      verified_by: context.userId,
    };
    const result = data.id
      ? await supabaseAdmin.from("mobility_resources").update(payload).eq("id", data.id).select("id").maybeSingle()
      : await supabaseAdmin
          .from("mobility_resources")
          .insert({ ...payload, created_by: context.userId })
          .select("id")
          .maybeSingle();
    if (result.error) throw new Error(result.error.message);
    await supabaseAdmin.from("mobility_resource_audit").insert({
      resource_id: result.data?.id ?? null,
      actor_user_id: context.userId,
      action: data.id ? "update" : "create",
      city_key: payload.city_key,
      mode: payload.mode,
      url: payload.url,
      metadata: { official: payload.official, active: payload.active },
    });
    cache.clear();
    return { ok: true, id: result.data?.id ?? null };
  });

export const setMobilityResourceActive = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ id: z.string().uuid(), active: z.boolean() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("mobility_resources")
      .update({ active: data.active })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("mobility_resource_audit").insert({
      resource_id: data.id,
      actor_user_id: context.userId,
      action: data.active ? "activate" : "deactivate",
      metadata: {},
    });
    cache.clear();
    return { ok: true };
  });

export const verifyMobilityResource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("mobility_resources")
      .update({ verified_at: new Date().toISOString(), verified_by: context.userId })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("mobility_resource_audit").insert({
      resource_id: data.id,
      actor_user_id: context.userId,
      action: "verify",
      metadata: {},
    });
    cache.clear();
    return { ok: true };
  });

/**
 * Importação em lote: uma linha por recurso. Valida tudo antes de gravar e
 * devolve as linhas rejeitadas com a razão — nunca corrige nem inventa dados.
 */
export const bulkImportMobilityResources = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ text: z.string().min(3).max(40000), dryRun: z.boolean().default(false) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.userId);
    const { parseBulkMobility } = await import("./bulk-import");
    const { rows, errors } = parseBulkMobility(data.text);
    if (data.dryRun || rows.length === 0) {
      return { imported: 0, rows: rows.length, errors };
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const now = new Date().toISOString();
    const payload = rows.map((r) => ({
      city_key: r.cityKey,
      city_label: r.city,
      mode: r.mode,
      provider_name: r.providerName,
      url: r.url,
      official: r.official,
      notes: r.notes ?? null,
      priority: 100,
      active: true,
      verified_at: now,
      verified_by: context.userId,
      created_by: context.userId,
    }));
    const { data: inserted, error } = await supabaseAdmin
      .from("mobility_resources")
      .insert(payload)
      .select("id");
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("mobility_resource_audit").insert({
      actor_user_id: context.userId,
      action: "bulk_import",
      metadata: { imported: inserted?.length ?? 0, rejected: errors.length },
    });
    cache.clear();
    return { imported: inserted?.length ?? 0, rows: rows.length, errors };
  });

/** Cidades com roteiros criados a que falta cobertura essencial. */
export const getMobilityCoverageGaps = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { coverageGaps } = await import("./bulk-import");
    const [{ data: saved }, { data: pro }, { data: existing }] = await Promise.all([
      supabaseAdmin.from("saved_itineraries").select("city").not("city", "is", null).limit(1000),
      supabaseAdmin.from("pro_itineraries").select("city").not("city", "is", null).limit(1000),
      supabaseAdmin.from("mobility_resources").select("city_key, mode, active").limit(2000),
    ]);
    const cities = [...(saved ?? []), ...(pro ?? [])]
      .map((r) => String((r as { city: string }).city))
      .filter(Boolean)
      .map((c) => ({ cityKey: resolveCityKey(c), cityLabel: c }));
    const gaps = coverageGaps(
      cities,
      (existing ?? []).map((r) => ({
        cityKey: String((r as { city_key: string }).city_key),
        mode: String((r as { mode: string }).mode),
        active: Boolean((r as { active: boolean }).active),
      })),
    );
    return { gaps: gaps.slice(0, 30) };
  });
